import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth, useUser } from '@clerk/clerk-react';
import StandardPicker from '../components/StandardPicker';
import OptionTagPicker from '../components/OptionTagPicker';
import { PPE_OPTIONS, COMPETENCY_OPTIONS } from '../components/qmsOptionLists';

import ProcedureAuthoringMetadata from '../components/ProcedureAuthoringMetadata';

const API_BASE = process.env.REACT_APP_API_URL;
const ACCENT = '#34d399';
const TEST_TYPES = ['Firmware Flash','Functional Test','Burn-In','Calibration','Visual Inspection',
  'Electrical Safety','Performance Test','Environmental Test','Software Test','Integration Test','Generic'];
const EVIDENCE_TYPES = ['Pass / Fail','Measurement','Photo','Log File','Signature','None'];
const SAMPLING_METHODS = ['100%','Sample','First-article','N/A'];
const SUPPLIERS = ['gpv','ubiqconn','acmepoint','hk_ringer'];
const SUPPLIER_LABELS = { gpv:'GPV', ubiqconn:'Ubiqconn', acmepoint:'Acmepoint', hk_ringer:'HK Ringer' };

function normalizeRole(r) {
  if (!r) return ''; if (Array.isArray(r)) return (r[0]||'').toLowerCase().trim();
  return String(r).toLowerCase().trim();
}

const BLANK_STEP = { instruction:'', expectedResult:'', acceptanceCriteria:'',
  evidenceRequired:false, evidenceType:'Pass / Fail', minValue:'', maxValue:'', unit:'',
  tolerance:'', measurementUncertainty:'', warningNote:'' };

export default function TestLibraryNew() {
  const { getToken } = useAuth(); const { user } = useUser(); const navigate = useNavigate();
  const role = normalizeRole(user?.publicMetadata?.role) || normalizeRole(user?.publicMetadata?.roles);
  const supplierKey = SUPPLIERS.includes(role.replace(/^test-/,'')) ? role.replace(/^test-/,'') : null;
  const canEdit = ['admin','quality-manager','production-engineer','service'].includes(role) || !!supplierKey;

  const [docNum, setDocNum] = useState('');
  const [form, setForm] = useState({
    name:'', testType:'Generic', description:'', passCriteria:'',
    estimatedDurationMinutes:'', firmwareVersion:'', testScriptVersion:'',
    purpose:'', scope:'', prerequisites:'', environmentalConditions:'',
    safetyPrecautions:'', requiredPPE:[], requiredCompetencies:[], referencedStandards:[],
    samplingPlan:{ method:'100%', sampleSize:'', aql:'' },
    onFailureAction:'', retestLimit:'', effectiveDate:'', nextReviewDate:'',
    recordsRetention:{ retentionPeriodMonths:'', storageLocation:'' },
    approval:{ preparedByName:'', reviewedByName:'', approvedByName:'' },
    applicability:'', sourceReferences:[], releaseBlockers:[],
    steps:[], notes:'',
    visibility:{ suppliers:[], supplierAccess:'None' },
  });
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    if (!canEdit) { navigate('/quality/test-library'); return; }
    getToken().then(token =>
      window.fetch(`${API_BASE}/quality/next-doc-number?prefix=TL-NIN-`, {
        headers:{ Authorization:`Bearer ${token}` },
      }).then(r=>r.json()).then(d=>{ if (d.ok) setDocNum(current => current || d.documentNumber); })
    );
  }, []);// eslint-disable-line

  const set = (k,v) => setForm(f=>({...f,[k]:v}));
  const toggleSupplier = s => {
    const cur = form.visibility.suppliers;
    const next = cur.includes(s)?cur.filter(x=>x!==s):[...cur,s];
    setForm(f=>({...f,visibility:{...f.visibility,suppliers:next,supplierAccess:next.length?'Specific':'None'}}));
  };
  const addStep = () => setForm(f=>({...f,steps:[...f.steps,{...BLANK_STEP,stepNumber:f.steps.length+1}]}));
  const removeStep = i => setForm(f=>({ ...f, steps: f.steps.filter((_,j)=>j!==i).map((s,j)=>({...s,stepNumber:j+1})) }));
  const setStep = (i,k,v) => setForm(f=>({ ...f, steps: f.steps.map((s,j)=>j===i?{...s,[k]:v}:s) }));

  const handleSubmit = async e => {
    e.preventDefault();
    if (!form.name.trim()) { setError('Name is required'); return; }
    setSaving(true); setError('');
    try {
      const token = await getToken();
      const payload = { ...form, documentNumber: docNum,
        estimatedDurationMinutes: form.estimatedDurationMinutes ? Number(form.estimatedDurationMinutes) : null,
        retestLimit: form.retestLimit ? Number(form.retestLimit) : null,
        effectiveDate: form.effectiveDate || null,
        nextReviewDate: form.nextReviewDate || null,
        samplingPlan: { method: form.samplingPlan.method,
          sampleSize: form.samplingPlan.sampleSize ? Number(form.samplingPlan.sampleSize) : null,
          aql: form.samplingPlan.aql || '' },
        recordsRetention: { retentionPeriodMonths: form.recordsRetention.retentionPeriodMonths ? Number(form.recordsRetention.retentionPeriodMonths) : null,
          storageLocation: form.recordsRetention.storageLocation || '' } };
      const res = await window.fetch(`${API_BASE}/quality/test-library`, {
        method:'POST', headers:{ Authorization:`Bearer ${token}`,'Content-Type':'application/json' },
        body: JSON.stringify(payload),
      });
      const data = await res.json();
      if (!data.ok) throw new Error(data.message||data.error||'Failed');
      navigate(`/quality/test-library/${data.entry._id}`);
    } catch(err) { setError(err.message); } finally { setSaving(false); }
  };

  const inp = { background:'var(--ni-panel)',border:'1px solid var(--ni-border-soft)',
    color:'var(--ni-text)',borderRadius:6,padding:'8px 12px',fontSize:13,width:'100%',boxSizing:'border-box' };
  const labelS = { display:'block',fontSize:11,fontWeight:700,letterSpacing:'0.06em',
    textTransform:'uppercase',color:'var(--ni-text-muted)',marginBottom:5 };

  return (
    <div style={{ maxWidth:760 }}>
      <div style={{ display:'flex',alignItems:'center',gap:12,marginBottom:24 }}>
        <button onClick={()=>navigate('/quality/test-library')}
          style={{ background:'none',border:'none',color:'var(--ni-text-muted)',cursor:'pointer',fontSize:20,padding:0 }}>←</button>
        <h2 style={{ margin:0,fontSize:20,fontWeight:800 }}>New Test Procedure</h2>
      </div>

      <form onSubmit={handleSubmit}>
        <div style={{ display:'grid',gridTemplateColumns:'1fr 1fr',gap:16,marginBottom:16 }}>
          <div><label style={labelS}>Document Number</label>
            <input aria-label="Document Number" value={docNum} onChange={e=>setDocNum(e.target.value)} required style={inp} /></div>
          <div><label style={labelS}>Test Type</label>
            <select value={form.testType} onChange={e=>set('testType',e.target.value)} style={inp}>
              {TEST_TYPES.map(t=><option key={t}>{t}</option>)}</select></div>
        </div>

        <div style={{ marginBottom:16 }}>
          <label style={labelS}>Name *</label>
          <input value={form.name} onChange={e=>set('name',e.target.value)} required
            placeholder="e.g. NFC Tag Functional Test Procedure" style={inp} />
        </div>

        <div style={{ marginBottom:16 }}>
          <label style={labelS}>Description</label>
          <textarea value={form.description} onChange={e=>set('description',e.target.value)}
            rows={3} style={{ ...inp,resize:'vertical' }} />
        </div>

        <div style={{ display:'grid',gridTemplateColumns:'1fr 1fr 1fr',gap:16,marginBottom:16 }}>
          <div><label style={labelS}>Est. Duration (min)</label>
            <input type="number" value={form.estimatedDurationMinutes} onChange={e=>set('estimatedDurationMinutes',e.target.value)} style={inp} /></div>
          <div><label style={labelS}>Firmware Version</label>
            <input value={form.firmwareVersion} onChange={e=>set('firmwareVersion',e.target.value)} style={inp} /></div>
          <div><label style={labelS}>Script Version</label>
            <input value={form.testScriptVersion} onChange={e=>set('testScriptVersion',e.target.value)} style={inp} /></div>
        </div>

        <div style={{ marginBottom:16 }}>
          <label style={labelS}>Pass Criteria (overall)</label>
          <textarea value={form.passCriteria} onChange={e=>set('passCriteria',e.target.value)}
            rows={2} style={{ ...inp,resize:'vertical' }} placeholder="What conditions define overall test pass?" />
        </div>

        {/* Procedure governance & acceptance (ISO/IEC 17025 / ISO 9001) */}
        <div style={{ background:'var(--ni-panel)',border:'1px solid var(--ni-border-soft)',borderRadius:10,padding:16,marginBottom:16 }}>
          <div style={{ ...labelS,marginBottom:12 }}>Procedure Governance &amp; Acceptance</div>
          <div style={{ display:'grid',gridTemplateColumns:'1fr 1fr',gap:12,marginBottom:12 }}>
            <div><div style={labelS}>Purpose</div>
              <textarea value={form.purpose} onChange={e=>set('purpose',e.target.value)} rows={2} style={{ ...inp,resize:'vertical' }} placeholder="Why this test exists" /></div>
            <div><div style={labelS}>Scope</div>
              <textarea value={form.scope} onChange={e=>set('scope',e.target.value)} rows={2} style={{ ...inp,resize:'vertical' }} placeholder="Where it applies / exclusions" /></div>
          </div>
          <div style={{ display:'grid',gridTemplateColumns:'1fr 1fr',gap:12,marginBottom:12 }}>
            <div><div style={labelS}>Prerequisites</div>
              <textarea value={form.prerequisites} onChange={e=>set('prerequisites',e.target.value)} rows={2} style={{ ...inp,resize:'vertical' }} placeholder="e.g. firmware loaded, warmed up 10 min" /></div>
            <div><div style={labelS}>Environmental Conditions</div>
              <input value={form.environmentalConditions} onChange={e=>set('environmentalConditions',e.target.value)} style={inp} placeholder="e.g. 23±2°C, 50% RH" /></div>
          </div>
          <div style={{ marginBottom:12 }}><div style={labelS}>Safety Precautions</div>
            <input value={form.safetyPrecautions} onChange={e=>set('safetyPrecautions',e.target.value)} style={inp} placeholder="Hazards / precautions" /></div>
          <div style={{ display:'grid',gridTemplateColumns:'1fr 1fr',gap:12,marginBottom:12 }}>
            <div><div style={labelS}>Required PPE</div>
              <OptionTagPicker value={form.requiredPPE} onChange={v=>set('requiredPPE',v)} options={PPE_OPTIONS} accent={ACCENT} placeholder="+ Add PPE…" /></div>
            <div><div style={labelS}>Required Competencies</div>
              <OptionTagPicker value={form.requiredCompetencies} onChange={v=>set('requiredCompetencies',v)} options={COMPETENCY_OPTIONS} accent={ACCENT} placeholder="+ Add competency…" /></div>
          </div>
          <div style={{ marginBottom:12 }}><div style={labelS}>Referenced Standards</div>
            <StandardPicker multi value={form.referencedStandards} onChange={v=>set('referencedStandards',v)} getToken={getToken} accent={ACCENT} /></div>
          <div style={{ display:'grid',gridTemplateColumns:'1fr 1fr 1fr',gap:12,marginBottom:12 }}>
            <div><div style={labelS}>Sampling Method</div>
              <select value={form.samplingPlan.method} onChange={e=>set('samplingPlan',{...form.samplingPlan,method:e.target.value})} style={inp}>
                {SAMPLING_METHODS.map(m=><option key={m}>{m}</option>)}</select></div>
            <div><div style={labelS}>Sample Size</div>
              <input type="number" value={form.samplingPlan.sampleSize} onChange={e=>set('samplingPlan',{...form.samplingPlan,sampleSize:e.target.value})} style={inp} /></div>
            <div><div style={labelS}>AQL</div>
              <input value={form.samplingPlan.aql} onChange={e=>set('samplingPlan',{...form.samplingPlan,aql:e.target.value})} style={inp} placeholder="e.g. 1.0" /></div>
          </div>
          <div style={{ display:'grid',gridTemplateColumns:'2fr 1fr',gap:12,marginBottom:12 }}>
            <div><div style={labelS}>On Failure — Reaction</div>
              <input value={form.onFailureAction} onChange={e=>set('onFailureAction',e.target.value)} style={inp} placeholder="e.g. quarantine + raise NCR" /></div>
            <div><div style={labelS}>Retest Limit</div>
              <input type="number" value={form.retestLimit} onChange={e=>set('retestLimit',e.target.value)} style={inp} /></div>
          </div>
          <div style={{ display:'grid',gridTemplateColumns:'1fr 1fr',gap:12,marginBottom:12 }}>
            <div><div style={labelS}>Effective Date</div>
              <input type="date" value={form.effectiveDate} onChange={e=>set('effectiveDate',e.target.value)} style={inp} /></div>
            <div><div style={labelS}>Next Review Date</div>
              <input type="date" value={form.nextReviewDate} onChange={e=>set('nextReviewDate',e.target.value)} style={inp} /></div>
          </div>
          <div style={{ display:'grid',gridTemplateColumns:'1fr 1fr',gap:12,marginBottom:12 }}>
            <div><div style={labelS}>Records Retention (months)</div>
              <input type="number" value={form.recordsRetention.retentionPeriodMonths} onChange={e=>set('recordsRetention',{...form.recordsRetention,retentionPeriodMonths:e.target.value})} style={inp} /></div>
            <div><div style={labelS}>Records Storage Location</div>
              <input value={form.recordsRetention.storageLocation} onChange={e=>set('recordsRetention',{...form.recordsRetention,storageLocation:e.target.value})} style={inp} placeholder="e.g. QMS / SharePoint" /></div>
          </div>
          <div style={{ display:'grid',gridTemplateColumns:'1fr 1fr 1fr',gap:12 }}>
            <div><div style={labelS}>Prepared by</div>
              <input value={form.approval.preparedByName} onChange={e=>set('approval',{...form.approval,preparedByName:e.target.value})} style={inp} /></div>
            <div><div style={labelS}>Reviewed by</div>
              <input value={form.approval.reviewedByName} onChange={e=>set('approval',{...form.approval,reviewedByName:e.target.value})} style={inp} /></div>
            <div><div style={labelS}>Approved by</div>
              <input value={form.approval.approvedByName} onChange={e=>set('approval',{...form.approval,approvedByName:e.target.value})} style={inp} /></div>
          </div>
        </div>

        {/* Test Steps */}
        <div style={{ marginBottom:20 }}>
          <div style={{ display:'flex',justifyContent:'space-between',alignItems:'center',marginBottom:10 }}>
            <label style={labelS}>Test Steps</label>
            <button type="button" onClick={addStep}
              style={{ background:'#34d39922',color:'#34d399',border:'1px solid #34d39944',borderRadius:6,
                padding:'4px 14px',fontSize:12,fontWeight:700,cursor:'pointer' }}>+ Step</button>
          </div>
          {form.steps.length===0 && (
            <div style={{ padding:'20px',textAlign:'center',color:'var(--ni-text-muted)',fontSize:13,
              border:'1px dashed var(--ni-border-soft)',borderRadius:8 }}>
              No steps yet. Click + Step to add the first test step.
            </div>
          )}
          {form.steps.map((step,i)=>(
            <div key={i} style={{ background:'var(--ni-panel)',border:'1px solid var(--ni-border-soft)',
              borderRadius:8,padding:14,marginBottom:10,position:'relative' }}>
              <div style={{ display:'flex',justifyContent:'space-between',alignItems:'center',marginBottom:10 }}>
                <span style={{ fontSize:12,fontWeight:700,color:'#34d399' }}>Step {step.stepNumber}</span>
                <button type="button" onClick={()=>removeStep(i)}
                  style={{ background:'none',border:'none',color:'#EC2124',cursor:'pointer',fontSize:16,padding:'0 4px' }}>×</button>
              </div>
              <div style={{ display:'grid',gridTemplateColumns:'1fr 1fr',gap:10,marginBottom:8 }}>
                <div><div style={labelS}>Instruction</div>
                  <textarea value={step.instruction} onChange={e=>setStep(i,'instruction',e.target.value)}
                    rows={2} style={{ ...inp,resize:'vertical' }} /></div>
                <div><div style={labelS}>Expected Result</div>
                  <textarea value={step.expectedResult} onChange={e=>setStep(i,'expectedResult',e.target.value)}
                    rows={2} style={{ ...inp,resize:'vertical' }} /></div>
              </div>
              <div style={{ display:'grid',gridTemplateColumns:'2fr 1fr',gap:10,marginBottom:8 }}>
                <div><div style={labelS}>Acceptance Criteria</div>
                  <input value={step.acceptanceCriteria} onChange={e=>setStep(i,'acceptanceCriteria',e.target.value)} style={inp} /></div>
                <div><div style={labelS}>Evidence Type</div>
                  <select value={step.evidenceType} onChange={e=>setStep(i,'evidenceType',e.target.value)} style={inp}>
                    {EVIDENCE_TYPES.map(t=><option key={t}>{t}</option>)}</select></div>
              </div>
              {step.evidenceType==='Measurement' && (
                <>
                  <div style={{ display:'grid',gridTemplateColumns:'1fr 1fr 1fr',gap:10,marginBottom:8 }}>
                    <div><div style={labelS}>Min</div><input type="number" value={step.minValue} onChange={e=>setStep(i,'minValue',e.target.value)} style={inp} /></div>
                    <div><div style={labelS}>Max</div><input type="number" value={step.maxValue} onChange={e=>setStep(i,'maxValue',e.target.value)} style={inp} /></div>
                    <div><div style={labelS}>Unit</div><input value={step.unit} onChange={e=>setStep(i,'unit',e.target.value)} style={inp} /></div>
                  </div>
                  <div style={{ display:'grid',gridTemplateColumns:'1fr 1fr',gap:10,marginBottom:8 }}>
                    <div><div style={labelS}>Tolerance</div><input value={step.tolerance} onChange={e=>setStep(i,'tolerance',e.target.value)} placeholder="e.g. ±0.2 V" style={inp} /></div>
                    <div><div style={labelS}>Measurement Uncertainty</div><input value={step.measurementUncertainty} onChange={e=>setStep(i,'measurementUncertainty',e.target.value)} placeholder="e.g. ±0.05 V (k=2)" style={inp} /></div>
                  </div>
                </>
              )}
              {step.warningNote!==undefined && (
                <div><div style={labelS}>Warning / Note</div>
                  <input value={step.warningNote} onChange={e=>setStep(i,'warningNote',e.target.value)}
                    placeholder="Safety note or warning (optional)" style={inp} /></div>
              )}
            </div>
          ))}
        </div>

        <ProcedureAuthoringMetadata value={form} editing onChange={set} staff={!supplierKey} />

        <div style={{ marginBottom:16 }}>
          <label style={labelS}>Notes</label>
          <textarea value={form.notes} onChange={e=>set('notes',e.target.value)}
            rows={2} style={{ ...inp,resize:'vertical' }} />
        </div>

        <div style={{ marginBottom:24 }}>
          <label style={labelS}>Supplier Visibility</label>
          {supplierKey ? (
            <div style={{ fontSize:13,color:'var(--ni-text-muted)',background:'#34d39911',
              border:'1px solid #34d39933',borderRadius:8,padding:'10px 12px',lineHeight:1.5 }}>
              🔒 Visible to North Invent and your organisation (<strong style={{ color:'#34d399' }}>{SUPPLIER_LABELS[supplierKey]}</strong>).
              North Invent reviews and releases your contribution — visibility is set automatically.
            </div>
          ) : (
            <div style={{ display:'flex',gap:8,flexWrap:'wrap' }}>
              {SUPPLIERS.map(s=>(
                <button type="button" key={s} onClick={()=>toggleSupplier(s)}
                  style={{ padding:'5px 14px',borderRadius:20,fontSize:12,fontWeight:700,cursor:'pointer',border:'1px solid',
                    background:form.visibility.suppliers.includes(s)?'#34d39922':'transparent',
                    color:form.visibility.suppliers.includes(s)?'#34d399':'var(--ni-text-muted)',
                    borderColor:form.visibility.suppliers.includes(s)?'#34d399':'var(--ni-border-soft)' }}>
                  {s.toUpperCase()}
                </button>
              ))}
            </div>
          )}
        </div>

        {error && <div style={{ color:'#EC2124',fontSize:13,marginBottom:12 }}>{error}</div>}
        <div style={{ display:'flex',gap:10 }}>
          <button type="submit" disabled={saving}
            style={{ background:'#34d399',color:'#000',border:'none',borderRadius:6,
              padding:'10px 24px',fontWeight:700,fontSize:13,cursor:saving?'wait':'pointer' }}>
            {saving?'Creating…':'Create Test Procedure'}
          </button>
          <button type="button" onClick={()=>navigate('/quality/test-library')}
            style={{ background:'transparent',border:'1px solid var(--ni-border-soft)',color:'var(--ni-text)',
              borderRadius:6,padding:'10px 20px',fontSize:13,cursor:'pointer' }}>Cancel</button>
        </div>
      </form>
    </div>
  );
}
